import { AfterContentInit, AfterViewInit, Component, ContentChildren, ElementRef, Input, NgZone, OnDestroy, OnInit, QueryList, Renderer2, ViewChild } from '@angular/core';
import { debounceTime, exhaustMap } from 'rxjs';
import { TabsService } from '../../../../shared/components/tabs/tabs.service';
import { CurrentTab } from '../../../../shared/model/enum';
import { ImportDocumentStep, IMPORT_DOCUMENT_STEPS } from '../../import-documents.model';
import { ImportDocumentsService } from '../../import-documents.service';
import { ProgressHelperService } from './progress-helper.service';
import { ProgressStepComponent } from './progress-step/progress-step.component';
import { Status, UiHelper } from './uiHelper';

@Component({
  selector: "app-step-progressbar",
  templateUrl: "./step-progressbar.component.html",
  styleUrls: ["./step-progressbar.component.scss"],
})
export class StepProgressbarComponent extends UiHelper implements OnInit, AfterContentInit, AfterViewInit, OnDestroy {
  @Input() public set selectedIndex(value: any) {
    this.activeIndex = value || 0;
    this.importDocumentsService.indexActive = this.activeIndex;
  }
  @ViewChild("stepContainer") stepContainer!: ElementRef;
  @ContentChildren(ProgressStepComponent) public steps!: QueryList<ProgressStepComponent>;
  tabName = CurrentTab;
  names = IMPORT_DOCUMENT_STEPS;
  importDocumentStep = ImportDocumentStep;
  status = Status;
  hasFailed = false;
  totalSteps: number = 0;
  currentTab: string = '';
  private resizeObserver: ResizeObserver;

  constructor(
    protected helperService: ProgressHelperService,
    private renderer: Renderer2,
    private elementRef: ElementRef,
    public importDocumentsService: ImportDocumentsService,
    public tabsService: TabsService,
    private ngZone: NgZone
  ) {
    super(helperService);
  }

  ngOnInit(): void {
    this.tabsService.currentTab.subscribe((res: string) => {
      if (res !== undefined) this.currentTab = res;
    });

    this.summaryTab();
    this.hasFailed = false;
    this.gridSection(this.activeIndex);
    this.progresshelper.eventHelper.pipe(debounceTime(300),exhaustMap(({ prev, next }) => {
      return new Promise<void>((resolve) => {
        if (next) this.next();
        if (prev) this.prev();
        setTimeout(resolve, 1000);
      });
    })).subscribe();
  }

  ngAfterContentInit(): void {
    this.initProgress(this.progressStep.length);
    this.setActiveStep(this.activeIndex);
    this.initStepIndex();
  }

  ngAfterViewInit() {
    // Defer one tick so the browser finishes full layout/paint before reading dimensions
    setTimeout(() => this.calculateWidth(), 0);

    // ResizeObserver watches the element itself — fires whenever ITS size changes,
    // not just window resize (catches grid/flex re-layouts, sidebar toggles, tab switches, etc.)
    // Run outside Angular zone to avoid triggering unnecessary change detection on every resize tick
    this.ngZone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => {
        // Re-enter zone only to push the new value (triggers [style.width.px] binding)
        this.ngZone.run(() => this.calculateWidth());
      });
      this.resizeObserver.observe(this.stepContainer.nativeElement);
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  public next() {
    this.increaseStep();
  }

  public prev() {
    this.decreaseStep();
  }

  calculateWidth() {
    const container = this.stepContainer?.nativeElement as HTMLElement;
    if (!container) return;
    const children = Array.from(container.children) as HTMLElement[];
    if (!children.length) return;
    // Measure from the LEFT edge of the first step circle to the RIGHT edge of the last step circle.
    // This matches exactly the visual span of the step row (what you see in DevTools per check-box),
    // excluding the container's own clamp() padding on both sides.
    const firstRect = children[0].getBoundingClientRect();
    const lastRect  = children[children.length - 1].getBoundingClientRect();
    const width = Math.round(lastRect.right - firstRect.left);
    this.helperService.actWidth.next(width);
  }

  gridSection(index: number) {
    this.renderer.setStyle(this.elementRef.nativeElement, "display", "grid");
  }

  summaryTab() {
    this.importDocumentsService.applyChanges.subscribe((res: any) => {
      if (res == true || res?.isSuccess == false) {
        this.next();
        this.hasFailed = true;
      } else if (res?.isSuccess == true && res?.response == null) {
        this.next();
        this.itemProgressList[5].status = "completed";
      }
    });
  }

  increaseStep() {
    if (
      this.activeIndex === this.totalSteps &&
      this.itemProgressList[this.activeIndex].status !== Status.COMPLETED
    ) {
      this.completeLastStep();
    }
    if (this.activeIndex < this.totalSteps - 1) {
      this.activeIndex++;
      this.importDocumentsService.indexActive = this.activeIndex;
      this.switchStatusNext(this.activeIndex);
      this.setActiveStep(this.activeIndex);
      this.gridSection(this.activeIndex);
    }
  }

  decreaseStep() {
    if (
      this.activeIndex === this.totalSteps - 1 &&
      this.itemProgressList[this.activeIndex].status === Status.COMPLETED
    ) {
      this.undoLastComplete();
    } else {
      if (this.activeIndex > 0) {
        this.activeIndex--;
        this.importDocumentsService.indexActive = this.activeIndex;
        this.switchStatusPrev(this.activeIndex);
        this.setActiveStep(this.activeIndex);
        this.gridSection(this.activeIndex);
      }
    }
  }

  removeActiveStep() {
    this.progressStep.map((step) => {
      if (step.isActive) step.isActive = false;
    });
  }

  updatedActiveStep(index: any) {
    this.progressStep[index].activeState = this.progressStep[index];
  }

  generatedProgressArray(
    length: number
  ): { stepIndex: number; status: string; names: string[] }[] {
    return [...Array(length).keys()].map((key) => {
      return {
        stepIndex: key,
        status: key === this.activeIndex ? Status.IN_PROGRESS : Status.PENDING,
        names: this.names,
      };
    });
  }

  private setActiveStep(activeIndex: number) {
    if (this.stepExists) {
      this.removeActiveStep();
      this.updatedActiveStep(activeIndex);
    }
  }

  private initStepIndex() {
    this.progressStep.forEach((step, i) => (step.stepIndex = i));
  }

  private initProgress(value: number) {
    this.totalSteps = value || 0;
    this.itemProgressList = this.generatedProgressArray(this.totalSteps);
  }

  public get activeStep(): ProgressStepComponent {
    return this.progressStep[this.activeIndex];
  }

  private get progressStep(): ProgressStepComponent[] {
    return this.steps.toArray();
  }

  private get stepExists(): boolean {
    return this.progressStep && Array.isArray(this.progressStep);
  }
}